import React from "react";

import { Grid, Skeleton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryLabel,
  VictoryStack,
} from "victory";
import { ChartData } from "libLegacy/api/models";
import {
  AXIS_STYLE,
  CHART_PADDING,
  useChartStyles,
  valuePlaceholder,
} from "./chartStyles";
import {
  formatTimeLabel,
  normalizeTimeValues,
  usePlaceholderLabels,
  formatKwhLabel,
} from "logic/Charts";
import { ChartCaption } from "./ChartCaption";

export type ChartConsumptionProps = {
  mainData: ChartData[];
  secondaryData: ChartData[];
  isLoading?: boolean;
  mainLabel: string;
  secondaryLabel: string;
};

export const ChartConsumption = ({
  isLoading,
  mainData,
  secondaryData,
  mainLabel,
  secondaryLabel,
}: ChartConsumptionProps) => {
  const { classes } = useChartStyles();
  const theme = useTheme();

  const COLOR_SCALE = [
    theme.palette.primary.main,
    theme.palette.secondary.light,
  ];

  const mainTotal = mainData.reduce(
    (mainTotal, { value }) => mainTotal + value,
    0
  );
  const secondaryTotal = secondaryData.reduce(
    (secondaryTotal, { value }) => secondaryTotal + value,
    0
  );

  const xTicks = mainData.map(normalizeTimeValues);

  const Chart = () => (
    <Grid
      container
      item
      className={classes.bordered}
      sx={{ mb: 1, flexGrow: 1 }}
    >
      <Grid container item>
        <VictoryChart padding={CHART_PADDING} domainPadding={6}>
          <VictoryAxis
            style={AXIS_STYLE}
            tickLabelComponent={<VictoryLabel angle={-45} dx={-8} dy={-5} />}
            tickFormat={formatTimeLabel}
            tickValues={xTicks}
          />
          <VictoryAxis
            dependentAxis
            style={AXIS_STYLE}
            tickFormat={formatKwhLabel}
            tickValues={
              usePlaceholderLabels(mainData) ? valuePlaceholder : undefined
            }
          />
          <VictoryStack colorScale={COLOR_SCALE}>
            {!!mainData?.length && (
              <VictoryBar
                data={mainData}
                x={normalizeTimeValues}
                y={({ value }) => +value}
                sortKey="x"
              />
            )}

            {!!secondaryData?.length && (
              <VictoryBar
                data={secondaryData}
                x={normalizeTimeValues}
                y={({ value }) => +value}
                sortKey="x"
              />
            )}
          </VictoryStack>
        </VictoryChart>
      </Grid>

      <Grid container item gap={1} justifyContent="center">
        {mainData.length > 0 && (
          <ChartCaption
            total={mainTotal}
            label={mainLabel}
            unit="kWh"
            color={COLOR_SCALE[0]}
          />
        )}
        {secondaryData.length > 0 && (
          <>
            <ChartCaption
              total={secondaryTotal}
              label={secondaryLabel}
              unit="kWh"
              color={COLOR_SCALE[1]}
            />
            <ChartCaption
              total={mainTotal + secondaryTotal}
              label="Total"
              unit="kWh"
            />
          </>
        )}
      </Grid>
    </Grid>
  );

  if (isLoading) {
    return (
      <Skeleton variant="rectangular">
        <Chart />
      </Skeleton>
    );
  } else return <Chart />;
};
